import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface NewsArticle {
  id: string
  title: string
  description: string | null
  author: string
  publish_date: string | null
  image_url: string | null
  article_file_url: string | null
  is_displayed: boolean
  expiry_days: number | null
}

interface UploadArticleModalProps {
  article?: NewsArticle | null
  onClose: () => void
  onSuccess: () => void
}

export default function UploadArticleModal({
  article,
  onClose,
  onSuccess,
}: UploadArticleModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [title, setTitle] = useState(article?.title || '')
  const [description, setDescription] = useState(article?.description || '')
  const [author, setAuthor] = useState(article?.author || '')
  const [publishDate, setPublishDate] = useState(
    article?.publish_date
      ? new Date(article.publish_date).toISOString().split('T')[0]
      : ''
  )
  const [isDisplayed, setIsDisplayed] = useState(article?.is_displayed ?? true)
  const [expiryDays, setExpiryDays] = useState<string>(
    article?.expiry_days?.toString() || (article ? '' : '30')
  )
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEditMode = !!article

  useEffect(() => {
    if (article) {
      setTitle(article.title)
      setDescription(article.description || '')
      setAuthor(article.author)
      setPublishDate(
        article.publish_date
          ? new Date(article.publish_date).toISOString().split('T')[0]
          : ''
      )
      setIsDisplayed(article.is_displayed ?? true)
      setExpiryDays(article.expiry_days?.toString() || '')
    } else {
      // Reset to defaults for new article
      setTitle('')
      setDescription('')
      setAuthor('')
      setPublishDate('')
      setIsDisplayed(true)
      setExpiryDays('30')
      setImageFile(null)
      setPdfFile(null)
    }
  }, [article])

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!user) return null

    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`
      const filePath = `news-images/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('news-images')
        .upload(filePath, file, { upsert: true })

      if (uploadError) {
        console.error('Error uploading image:', uploadError)
        throw new Error(`Failed to upload image: ${uploadError.message}`)
      }

      const { data } = supabase.storage
        .from('news-images')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (err) {
      console.error('Error uploading image:', err)
      throw err
    }
  }

  const uploadPdf = async (file: File): Promise<string | null> => {
    if (!user) return null

    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`
      const filePath = `news-articles/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('news-articles')
        .upload(filePath, file, { upsert: true })

      if (uploadError) {
        console.error('Error uploading PDF:', uploadError)
        throw new Error(`Failed to upload PDF: ${uploadError.message}`)
      }

      const { data } = supabase.storage
        .from('news-articles')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (err) {
      console.error('Error uploading PDF:', err)
      throw err
    }
  }

  const createArticleMutation = useMutation({
    mutationFn: async (articleData: {
      title: string
      description: string | null
      author: string
      publish_date: string | null
      image_url: string | null
      article_file_url: string | null
      is_displayed: boolean
      expiry_days: number | null
      created_by: string
    }) => {
      const { data, error } = await supabase
        .from('news_articles')
        .insert([articleData])
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to create article: ${error.message}`)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-articles'] })
      queryClient.invalidateQueries({ queryKey: ['admin-news-articles'] })
      onSuccess()
      onClose()
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  const updateArticleMutation = useMutation({
    mutationFn: async (articleData: {
      title: string
      description: string | null
      author: string
      publish_date: string | null
      image_url: string | null
      article_file_url: string | null
      is_displayed: boolean
      expiry_days: number | null
    }) => {
      if (!article) throw new Error('No article to update')

      const { data, error } = await supabase
        .from('news_articles')
        .update(articleData)
        .eq('id', article.id)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to update article: ${error.message}`)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-articles'] })
      queryClient.invalidateQueries({ queryKey: ['admin-news-articles'] })
      onSuccess()
      onClose()
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!title.trim() || !author.trim()) {
      setError('Title and Author are required')
      return
    }

    if (!isEditMode && !imageFile && !pdfFile) {
      setError('Please upload at least an image or PDF file')
      return
    }

    setIsUploading(true)

    try {
      let imageUrl: string | null = article?.image_url || null
      let pdfUrl: string | null = article?.article_file_url || null

      // Upload new image if provided
      if (imageFile) {
        imageUrl = await uploadImage(imageFile)
      }

      // Upload new PDF if provided
      if (pdfFile) {
        pdfUrl = await uploadPdf(pdfFile)
      }

      // Validate expiry_days if provided
      let expiryDaysValue: number | null = null
      if (expiryDays.trim()) {
        const parsed = parseInt(expiryDays, 10)
        if (isNaN(parsed) || parsed < 1) {
          setError('Expiry days must be a positive number')
          setIsUploading(false)
          return
        }
        expiryDaysValue = parsed
      }

      const articleData = {
        title: title.trim(),
        description: description.trim() || null,
        author: author.trim(),
        publish_date: publishDate ? new Date(publishDate).toISOString() : null,
        image_url: imageUrl,
        article_file_url: pdfUrl,
        is_displayed: isDisplayed,
        expiry_days: expiryDaysValue,
      }

      if (isEditMode) {
        await updateArticleMutation.mutateAsync(articleData)
      } else {
        await createArticleMutation.mutateAsync({
          ...articleData,
          created_by: user!.id,
        })
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred'
      setError(errorMessage)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-article-title"
    >
      <div
        className="rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2
            id="upload-article-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            {isEditMode ? 'Edit Article' : 'Upload News Article'}
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
            disabled={isUploading}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {error && (
          <div
            className="p-4 mb-6 rounded border-2"
            style={{
              backgroundColor: 'var(--color-error)',
              borderColor: 'var(--color-error)',
              color: 'var(--text-inverse)',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="title"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Title <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full px-4 py-2 rounded border-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              placeholder="Enter article title"
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 rounded border-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              placeholder="Enter article description"
            />
          </div>

          <div>
            <label
              htmlFor="author"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Author <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="text"
              id="author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              required
              className="w-full px-4 py-2 rounded border-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              placeholder="Enter author name"
            />
          </div>

          <div>
            <label
              htmlFor="publishDate"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Publish Date
            </label>
            <input
              type="date"
              id="publishDate"
              value={publishDate}
              onChange={(e) => setPublishDate(e.target.value)}
              className="w-full px-4 py-2 rounded border-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          <div>
            <label
              htmlFor="isDisplayed"
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                id="isDisplayed"
                checked={isDisplayed}
                onChange={(e) => setIsDisplayed(e.target.checked)}
                className="w-5 h-5"
                style={{
                  accentColor: 'var(--bg-interactive)',
                }}
              />
              <span
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                Display Article
              </span>
            </label>
            <p
              className="text-sm mt-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              When unchecked, the article will be hidden from public view
            </p>
          </div>

          <div>
            <label
              htmlFor="expiryDays"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Expiry Days (Optional)
            </label>
            <input
              type="number"
              id="expiryDays"
              min="1"
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}
              className="w-full px-4 py-2 rounded border-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              placeholder="Number of days after publish date to hide"
            />
            <p
              className="text-sm mt-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Leave empty for no automatic expiry. When set, the article will be automatically hidden after this many days from the publish date.
            </p>
          </div>

          <div>
            <label
              htmlFor="imageFile"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Article Image {isEditMode && '(leave empty to keep current)'}
            </label>
            <input
              type="file"
              id="imageFile"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              className="w-full px-4 py-2 rounded border-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
            />
            {imageFile && (
              <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                Selected: {imageFile.name}
              </p>
            )}
            {isEditMode && article?.image_url && !imageFile && (
              <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                Current image: <a href={article.image_url} target="_blank" rel="noopener noreferrer" className="underline">View</a>
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="pdfFile"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Article PDF {isEditMode && '(leave empty to keep current)'}
            </label>
            <input
              type="file"
              id="pdfFile"
              accept="application/pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              className="w-full px-4 py-2 rounded border-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
            />
            {pdfFile && (
              <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                Selected: {pdfFile.name}
              </p>
            )}
            {isEditMode && article?.article_file_url && !pdfFile && (
              <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                Current PDF: <a href={article.article_file_url} target="_blank" rel="noopener noreferrer" className="underline">View</a>
              </p>
            )}
          </div>

          <div className="flex gap-4 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-6 py-2 font-semibold rounded"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="px-6 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: isUploading
                  ? 'var(--bg-tertiary)'
                  : 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
            >
              {isUploading
                ? 'Uploading...'
                : isEditMode
                ? 'Update Article'
                : 'Upload Article'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

